import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { PageScrollArea } from '@/components/ui/page-shell';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { SequenceStep } from '@/lib/services/campaigns';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  MeasuringStrategy,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { StepCard, StepCardOverlay } from './StepCard';
import { StepEditorPanel } from './StepEditorPanel';

interface ValidationError {
  stepId: string;
  stepIndex: number;
  field: 'subject' | 'content' | 'senderEmail';
  message: string;
}

interface Lead {
  id: number | string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  linkedinUrl: string | null;
}

interface SequenceTabProps {
  sequence: SequenceStep[];
  setSequence: (sequence: SequenceStep[]) => void;
  selectedStepId: string;
  setSelectedStepId: (id: string) => void;
  emailAccounts: any[];
  isReadOnly?: boolean;
  hideEditWarning?: boolean;
  onHideEditWarningChange?: (hide: boolean) => void;
  validationErrors?: ValidationError[];
  isNewCampaign?: boolean;
  initialFirstStepId?: string | null;
  leads?: Lead[];
  senderEmails?: string[];
  onChangeSenderEmails?: (next: string[]) => void | Promise<void>;
}

export function SequenceTab({
  sequence,
  setSequence,
  selectedStepId,
  setSelectedStepId,
  emailAccounts,
  isReadOnly = false,
  hideEditWarning = false,
  onHideEditWarningChange,
  validationErrors = [],
  isNewCampaign = false,
  initialFirstStepId = null,
  leads = [],
  senderEmails = [],
  onChangeSenderEmails,
}: SequenceTabProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [wasSelectedBeforeDrag, setWasSelectedBeforeDrag] = useState(false);
  const [newlyAddedStepId, setNewlyAddedStepId] = useState<string | null>(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  // Track steps that were just created and haven't been left yet - these get a grace period
  const [newlyCreatedStepIds, setNewlyCreatedStepIds] = useState<Set<string>>(new Set());
  const [previousSelectedId, setPreviousSelectedId] = useState<string | null>(null);

  // When user leaves a newly created step, remove it from the grace period set
  useEffect(() => {
    if (previousSelectedId && previousSelectedId !== selectedStepId) {
      // If the previous step was newly created, remove it from the grace period
      if (newlyCreatedStepIds.has(previousSelectedId)) {
        setNewlyCreatedStepIds(prev => {
          const next = new Set(prev);
          next.delete(previousSelectedId);
          return next;
        });
      }
    }
    setPreviousSelectedId(selectedStepId);
  }, [selectedStepId, previousSelectedId, newlyCreatedStepIds]);

  // Show/hide warning modal based on read-only mode
  useEffect(() => {
    if (isReadOnly && !hideEditWarning) {
      setShowWarningModal(true);
    } else {
      // Auto-close modal when campaign is paused (no longer read-only) or when hideEditWarning is true
      setShowWarningModal(false);
    }
  }, [isReadOnly, hideEditWarning]);

  const handleCloseWarningModal = () => {
    setShowWarningModal(false);
  };

  const handleDontShowAgainChange = async (checked: boolean) => {
    if (onHideEditWarningChange) {
      // Save immediately when checked or unchecked
      await onHideEditWarningChange(checked);
      // If checked, close the modal immediately since hideEditWarning will prevent it from showing again
      if (checked) {
        setShowWarningModal(false);
      }
    }
  };

  const activeStep = activeId ? sequence.find(step => step.id === activeId) : null;
  const selectedStep = sequence.find(step => step.id === selectedStepId);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getDelayOptions = () => ([
    { value: '1', label: 'Wait 1 day before sending' },
    { value: '2', label: 'Wait 2 days before sending' },
    { value: '3', label: 'Wait 3 days before sending' },
    { value: '4', label: 'Wait 4 days before sending' },
    { value: '5', label: 'Wait 5 days before sending' },
    { value: '6', label: 'Wait 6 days before sending' },
    { value: '7', label: 'Wait 1 week before sending' },
    { value: '14', label: 'Wait 2 weeks before sending' },
  ]);

  const handleDragStart = (event: DragStartEvent) => {
    if (isReadOnly) return;
    const draggedStepId = event.active.id as string;
    setActiveId(draggedStepId);
    setWasSelectedBeforeDrag(selectedStepId === draggedStepId);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (isReadOnly) return;
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSequence(
        arrayMove(
          sequence,
          sequence.findIndex((item) => item.id === active.id),
          sequence.findIndex((item) => item.id === over.id)
        )
      );
    }
    
    setSelectedStepId(active.id as string);
    setActiveId(null);
  };

  const addStep = () => {
    if (isReadOnly) return;
    const newStep: SequenceStep = {
      id: Date.now().toString(),
      type: 'email',
      delay: 1,
      delayUnit: 'days',
      subject: '',
      content: ''
    };
    setSequence([...sequence, newStep]);
    setSelectedStepId(newStep.id);
    setNewlyAddedStepId(newStep.id);
    // Give the new step a grace period before showing validation errors
    setNewlyCreatedStepIds(prev => new Set([...prev, newStep.id]));
    setTimeout(() => {
      setNewlyAddedStepId(null);
    }, 600);
  };

  const deleteStep = (stepId: string) => {
    if (isReadOnly) return;
    if (sequence.length <= 1) {
      toast.error('Cannot delete the last email step');
      return;
    }
    
    const stepIndex = sequence.findIndex(step => step.id === stepId);
    const newSequence = sequence.filter(step => step.id !== stepId);
    setSequence(newSequence);
    
    if (selectedStepId === stepId && newSequence.length > 0) {
      const newSelectedIndex = stepIndex > 0 ? stepIndex - 1 : newSequence.length - 1;
      setSelectedStepId(newSequence[newSelectedIndex].id);
    }
    
    toast.success('Email step deleted');
  };

  const duplicateStep = (stepId: string) => {
    if (isReadOnly) return;
    const stepToDuplicate = sequence.find(step => step.id === stepId);
    if (stepToDuplicate) {
      const newStep: SequenceStep = {
        ...stepToDuplicate,
        id: Date.now().toString(),
        subject: `${stepToDuplicate.subject} (Copy)`
      };
      const stepIndex = sequence.findIndex(step => step.id === stepId);
      const newSequence = [...sequence];
      newSequence.splice(stepIndex + 1, 0, newStep);
      setSequence(newSequence);
      setSelectedStepId(newStep.id);
      toast.success('Email step duplicated');
    }
  };

  const updateSelectedStep = (updates: Partial<SequenceStep>) => {
    setSequence(sequence.map(step => 
      step.id === selectedStepId ? { ...step, ...updates } : step
    ));
  };

  return (
    <div className="flex gap-gutter h-full relative">
      {/* Read-only warning modal - centered on panel with blur */}
      {showWarningModal && (
        <div className="absolute inset-0 z-30 flex items-center justify-center">
          {/* Blur backdrop - extends to cover Card padding */}
          <div className="absolute -inset-5 bg-scrim-glass backdrop-blur-sm rounded-panel" />
          
          {/* Modal */}
          <div className="relative bg-popover rounded-card shadow-xl p-6 max-w-md mx-4">
            <h2 className="text-lg font-semibold text-center mb-3 mt-3">Campaign is Active</h2>
            <p className="text-sm text-foreground text-center mb-4">
              This campaign is currently running. You'll need to pause the campaign before you can edit the email sequence.
            </p>
            <div className="flex items-center justify-center space-x-0 mb-4">
              <Checkbox 
                id="dont-show-again" 
                checked={hideEditWarning}
                onCheckedChange={(checked) => handleDontShowAgainChange(checked === true)}
              />
              <label 
                htmlFor="dont-show-again" 
                className="text-sm text-foreground cursor-pointer"
              >
                Don't show this again for this campaign
              </label>
            </div>
            <div className="flex justify-center mb-3">
              <Button variant="primary" onClick={handleCloseWarningModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Sequence Builder */}
      <div className="flex-1 min-w-[250px] max-w-[50%] relative flex flex-col">
      <PageScrollArea surface="panel">
        <div className="py-6 px-6 pb-20">
        <div className="relative mt-0 w-full max-w-[340px] mx-auto">
          <div className="text-center mt-8 mb-0 relative z-10">
            <p className="text-xs text-content-body-strong mb-3">Email Sequence Start</p>
          </div>
          
          <div className="relative">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              measuring={{
                droppable: {
                  strategy: MeasuringStrategy.Always,
                },
              }}
            >
              <SortableContext
                items={sequence.map((step) => step.id)}
                strategy={verticalListSortingStrategy}
              >
                {sequence.map((step, index) => {
                  // Grace period: newly created steps OR the initial first step of a brand new campaign
                  // Using initialFirstStepId (captured when campaign loads) instead of index to ensure grace period
                  // only applies to the original step, not any step that happens to be at index 0
                  const isInGracePeriod = newlyCreatedStepIds.has(step.id) || (isNewCampaign && initialFirstStepId && step.id === initialFirstStepId);
                  const stepErrors = validationErrors.filter(e => e.stepId === step.id);
                  const hasStepError = !isInGracePeriod && stepErrors.length > 0;
                  const missingSubject = !isInGracePeriod && stepErrors.some(e => e.field === 'subject');
                  const missingContent = !isInGracePeriod && stepErrors.some(e => e.field === 'content');
                  const missingSender = !isInGracePeriod && stepErrors.some(e => e.field === 'senderEmail');
                  
                  return (
                    <StepCard
                      key={step.id}
                      step={step}
                      index={index}
                      isSelected={selectedStepId === step.id}
                      onSelect={() => setSelectedStepId(step.id)}
                      onDelete={() => deleteStep(step.id)}
                      onDuplicate={() => duplicateStep(step.id)}
                      sequence={sequence}
                      setSequence={setSequence}
                      getDelayOptions={getDelayOptions}
                      isAnyDragging={activeId !== null}
                      isNewlyAdded={newlyAddedStepId === step.id}
                      isReadOnly={isReadOnly}
                      hasError={hasStepError}
                      missingSubject={missingSubject}
                      missingContent={missingContent}
                      missingSender={missingSender}
                    />
                  );
                })}
              </SortableContext>
              <DragOverlay>
                {activeStep ? (() => {
                  const isInGracePeriod = newlyCreatedStepIds.has(activeStep.id) || (isNewCampaign && initialFirstStepId && activeStep.id === initialFirstStepId);
                  const stepErrors = validationErrors.filter(e => e.stepId === activeStep.id);
                  const hasStepError = !isInGracePeriod && stepErrors.length > 0;
                  const missingSubject = !isInGracePeriod && stepErrors.some(e => e.field === 'subject');
                  const missingContent = !isInGracePeriod && stepErrors.some(e => e.field === 'content');
                  const missingSender = !isInGracePeriod && stepErrors.some(e => e.field === 'senderEmail');

                  return (
                    <StepCardOverlay
                      step={activeStep}
                      wasSelected={wasSelectedBeforeDrag}
                      getDelayOptions={getDelayOptions}
                      hasError={hasStepError}
                      missingSubject={missingSubject}
                      missingContent={missingContent}
                      missingSender={missingSender}
                    />
                  );
                })() : null}
              </DragOverlay>
            </DndContext>
          </div>
          
          {!isReadOnly && (
            <Button
              variant="primary"
              onClick={addStep}
              className="flex items-center gap-2 mx-auto mt-5 shadow-xl"
            >
              Add Email Send
            </Button>
          )}
        </div>
        </div>
      </PageScrollArea>
      </div>

      {/* Step Editor Panel */}
      <StepEditorPanel
        selectedStep={selectedStep}
        emailAccounts={emailAccounts}
        onUpdateStep={updateSelectedStep}
        isReadOnly={isReadOnly}
        validationErrors={validationErrors.filter(e => {
          // Filter out errors for steps in grace period
          if (newlyCreatedStepIds.has(e.stepId)) return false;
          // Also filter out initial first step on first view
          if (isNewCampaign && initialFirstStepId && e.stepId === initialFirstStepId) return false;
          return true;
        })}
        leads={leads}
        senderEmails={senderEmails}
        onChangeSenderEmails={onChangeSenderEmails}
      />
    </div>
  );
}

